import { useAppSelector, useAppDispatch } from 'hooks';
import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import {
  Text,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
} from '@chakra-ui/react';

import Card from 'components/card/Card';

import FormInput from 'components/fields/home/FormInput';
import SelectInput from 'components/fields/home/SelectInput';
import SelectSearch from 'components/fields/SelectSearch';
import useIsBinderDisabled from 'views/home_form/hooks/useIsBinderDisabled';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

import {
  changeBrokerInformation,
  addMissingMandatoryField,
  removeMissingMandatoryField,
} from 'reducers/homePersonalFormReducer';

import { SECTIONS } from 'views/home_form/constants';

import type { APIBranchData, APIProducerCodeData } from 'types/api.types';

import { apiGetMyBranches, apiGetMyProducerCodes } from 'api';

const BrokerInformation = () => {
  const dispatch = useAppDispatch();

  const isBinderDisabled = useIsBinderDisabled();

  const isBranchWideCodesEnabled = useFeatureFlag(
    'enable-branch-wide-producer-codes',
  );

  const {
    data: producerCodes = [],
    isLoading: loadingProducerCodes,
    isFetching: isFetchingProducerCodes,
  } = useQuery<APIProducerCodeData[]>({
    queryKey: ['producer-codes-all'],
    queryFn: () => apiGetMyProducerCodes(null, 'all'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const {
    data: myBranches = [],
    isLoading: loadingMyBranches,
    isFetching: isFetchingMyBranches,
  } = useQuery<APIBranchData[]>({
    queryKey: ['my-branches-all'],
    queryFn: () => apiGetMyBranches(null, 'all'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const data = useAppSelector(
    (state) => state.homePersonalForm.data?.broker_information,
  );

  const isLOA = useAppSelector(
    (state) => state.homePersonalForm.data?.policy_information?.is_loa,
  );

  useEffect(() => {
    if (
      isBranchWideCodesEnabled &&
      (!data?.producer_code || data.producer_code === '')
    ) {
      dispatch(
        addMissingMandatoryField({
          name: 'Producer Code',
          section: SECTIONS.BROKER_INFORMATION,
        }),
      );
    } else if (isBranchWideCodesEnabled) {
      dispatch(removeMissingMandatoryField('Producer Code'));
    }
  }, [data?.producer_code, isBranchWideCodesEnabled, dispatch]);

  const onBrokerInformationChange = (event: any) => {
    const { name, value, checked, type } = event.target;
    const payload = type === 'checkbox' ? checked : value;

    dispatch(changeBrokerInformation({ name, value: payload }));
  };

  const hasBrokerCode = Boolean(data?.broker_code?.trim());

  if (!data) return null;

  return (
    <Card mb="20px" id="sectionBrokerInformation">
      <Text fontWeight="bold" fontSize="2xl" mb="20px">
        Broker Information
      </Text>

      <SimpleGrid columns={5} spacing="20px">
        <FormInput
          section={SECTIONS.BROKER_INFORMATION}
          label="Name"
          name="name"
          value={data.name || ''}
          onChange={onBrokerInformationChange}
          disabled
        />
        <FormInput
          section={SECTIONS.BROKER_INFORMATION}
          label="Contact Name"
          name="contact_name"
          value={data.contact_name || ''}
          onChange={onBrokerInformationChange}
          disabled
        />
        <FormInput
          section={SECTIONS.BROKER_INFORMATION}
          label="Email"
          name="email"
          value={data.email || ''}
          onChange={onBrokerInformationChange}
          disabled
        />
        <FormInput
          section={SECTIONS.BROKER_INFORMATION}
          label="Address"
          name="address"
          value={data.address || ''}
          onChange={onBrokerInformationChange}
          disabled
        />
        <FormInput
          section={SECTIONS.BROKER_INFORMATION}
          label="City"
          name="city"
          value={data.city || ''}
          onChange={onBrokerInformationChange}
          disabled
        />
        <FormInput
          section={SECTIONS.BROKER_INFORMATION}
          label="Province"
          name="province_code"
          value={data.province_code || ''}
          onChange={onBrokerInformationChange}
          disabled
        />
        <FormInput
          section={SECTIONS.BROKER_INFORMATION}
          label="Postal Code"
          name="postal_code"
          value={data.postal_code || ''}
          onChange={onBrokerInformationChange}
          disabled
        />
        {(() => {
          const branchesLoading = loadingMyBranches || isFetchingMyBranches;
          const currentBranch = data.branch || '';
          const initialBranchMissing =
            !branchesLoading &&
            !!currentBranch &&
            !myBranches.some((branch) => branch.name === currentBranch);

          return (
            <FormControl>
              <FormLabel ml="0">Branch</FormLabel>
              {initialBranchMissing ? (
                <>
                  <Input value={currentBranch} isDisabled />
                  <Text color="red.500" fontSize="sm" mt={1}>
                    You cannot change the branch because the initially selected
                    branch does not match your available branches.
                  </Text>
                </>
              ) : (
                <SelectSearch
                  name="branch"
                  value={currentBranch}
                  onChange={(value) => {
                    const event = {
                      target: {
                        name: 'branch',
                        value: value || '',
                        type: 'text',
                      },
                    };
                    onBrokerInformationChange(event);
                  }}
                  options={myBranches.map((branch) => ({
                    value: branch.name,
                    label: branch.name,
                  }))}
                  placeholder="Search..."
                  isDisabled={branchesLoading || myBranches.length <= 1}
                  isLoading={branchesLoading}
                />
              )}
            </FormControl>
          );
        })()}
        <FormInput
          section={SECTIONS.BROKER_INFORMATION}
          label="Business Phone"
          name="phone_business"
          value={data.phone_business || ''}
          onChange={onBrokerInformationChange}
        />
        <FormInput
          section={SECTIONS.BROKER_INFORMATION}
          label="Cell Phone"
          name="phone_cell"
          value={data.phone_cell || ''}
          onChange={onBrokerInformationChange}
        />
        <FormInput
          section={SECTIONS.BROKER_INFORMATION}
          label="Contract Number"
          name="contract_nr"
          value={data.contract_nr || ''}
          onChange={onBrokerInformationChange}
          required={isLOA && !hasBrokerCode}
        />
        <FormInput
          section={SECTIONS.BROKER_INFORMATION}
          label="Sub Contract Number"
          name="sub_contract_nr"
          value={data.sub_contract_nr || ''}
          onChange={onBrokerInformationChange}
        />
        <FormInput
          section={SECTIONS.BROKER_INFORMATION}
          label="Client ID"
          name="client_id"
          value={data.client_id || ''}
          onChange={onBrokerInformationChange}
        />
        <FormInput
          section={SECTIONS.BROKER_INFORMATION}
          label="Group Name"
          name="group_name"
          value={data.group_name || ''}
          onChange={onBrokerInformationChange}
        />
        <FormInput
          section={SECTIONS.BROKER_INFORMATION}
          label="Group ID"
          name="group_id"
          value={data.group_id || ''}
          onChange={onBrokerInformationChange}
        />
        <FormInput
          section={SECTIONS.BROKER_INFORMATION}
          label="Broker Code"
          name="broker_code"
          value={data.broker_code || ''}
          onChange={onBrokerInformationChange}
          disabled
        />
        {isBinderDisabled ? (
          <FormInput
            section={SECTIONS.BROKER_INFORMATION}
            label="Producer Code"
            name="producer_code"
            value={data.producer_code || ''}
            onChange={onBrokerInformationChange}
            required
            disabled
          />
        ) : isBranchWideCodesEnabled ? (
          <FormControl isRequired>
            <div id="Producer Code">
              <FormLabel ml="0">Producer Code</FormLabel>
              <SelectSearch
                name="producer_code"
                value={data.producer_code || ''}
                onChange={(value) => {
                  const event = {
                    target: {
                      name: 'producer_code',
                      value: value || '',
                      type: 'text',
                    },
                  };
                  onBrokerInformationChange(event);
                }}
                options={producerCodes.map((producerCode) => ({
                  value: producerCode.name,
                  label: producerCode.name,
                }))}
                placeholder="Search..."
                isDisabled={loadingProducerCodes || isFetchingProducerCodes}
                isLoading={loadingProducerCodes || isFetchingProducerCodes}
              />
            </div>
          </FormControl>
        ) : (
          <SelectInput
            section={SECTIONS.BROKER_INFORMATION}
            label="Producer Code"
            name="producer_code"
            value={data.producer_code || ''}
            options={[
              { value: '', label: 'Select...' },
              ...producerCodes.map((producerCode) => ({
                value: producerCode.name,
                label: producerCode.name,
              })),
            ]}
            onChange={onBrokerInformationChange}
            required
            disabled={loadingProducerCodes || isFetchingProducerCodes}
          />
        )}
      </SimpleGrid>
    </Card>
  );
};

export default BrokerInformation;
